import React, { useState } from 'react'
import { useDraggable } from '@dnd-kit/core'
import { CSS } from '@dnd-kit/utilities'
import { Edit2, Trash2, Calendar, DollarSign, Tag, Copy, Eye } from 'lucide-react'
import { Activation } from '../../types/activation'
import { Tooltip } from '../ui'
import { getPartnerLogoUrl } from '../../services/partnerService'

interface ActivationCardProps {
  activation: Activation
  onEdit: (activation: Activation) => void
  onDelete: (activation: Activation) => void
  onDuplicate?: (activation: Activation) => void
  onPartnerClick?: (partner: { id: string; name: string; logo_url?: string | null }) => void
  /** Handler for viewing insights */
  onViewInsights?: (activation: Activation) => void
}

const ActivationCard: React.FC<ActivationCardProps> = ({
  activation,
  onEdit,
  onDelete,
  onDuplicate,
  onPartnerClick,
  onViewInsights
}) => {
  const [imageError, setImageError] = useState(false)
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: activation.id,
    data: { activation }
  })

  const style = {
    transform: CSS.Translate.toString(transform),
    opacity: isDragging ? 0.5 : 1
  }

  const formatCurrency = (value: number | null) => {
    if (value === null) return '-'
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value)
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  }

  const partner = activation.partners
  const partnerName = partner?.name
  // Use proxy URL for partner logo to avoid Azure Storage public access issues
  const partnerLogoUrl = partner?.id && partner?.logo_url
    ? getPartnerLogoUrl(partner.id, activation.customer_id)
    : null

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      className={`bg-white rounded-lg border border-gray-200 p-2.5 shadow-sm hover:shadow-md transition-all duration-200 cursor-grab active:cursor-grabbing group ${
        isDragging ? 'ring-2 ring-blue-500 ring-opacity-50' : ''
      }`}
    >
      {/* Header - Name and Partner */}
      <div className="mb-1.5">
        <h4 className="text-xs font-medium text-gray-900 line-clamp-2 leading-tight">{activation.name}</h4>
        {partnerName && (
          <p className="text-[10px] text-gray-500 truncate mt-0.5">{partnerName}</p>
        )}
      </div>

      {/* Middle row - Type badge and Deal Value */}
      <div className="flex items-center flex-wrap gap-1.5 mb-1.5">
        {activation.activation_types && (
          <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-purple-100 text-purple-800">
            <Tag className="h-2 w-2 mr-0.5" />
            {activation.activation_types.name}
          </span>
        )}
        {activation.deal_value && (
          <span className="inline-flex items-center text-[10px] font-medium text-green-700">
            <DollarSign className="h-2.5 w-2.5" />
            {formatCurrency(activation.deal_value)}
          </span>
        )}
      </div>

      {/* Date Range */}
      {(activation.start_date || activation.end_date) && (
        <div className="flex items-center text-[10px] text-gray-500 mb-1.5">
          <Calendar className="h-2.5 w-2.5 mr-0.5" />
          <span>
            {formatDate(activation.start_date)} - {formatDate(activation.end_date)}
          </span>
        </div>
      )}

      {/* EMV Progress Bar - only show for Active/Completed activations with dates */}
      {activation.total_emv != null && activation.deal_value && activation.deal_value > 0 &&
       activation.start_date && activation.end_date &&
       (activation.status === 'Active' || activation.status === 'Completed' || activation.status === 'Suspended') && (
        <div className="mb-1.5">
          {(() => {
            const totalEmv = activation.total_emv || 0
            const targetValue = activation.emv_target_value || activation.deal_value
            const progressPercentage = Math.min(Math.round((totalEmv / targetValue) * 100), 100)
            const progressColor = progressPercentage >= 80 ? 'bg-green-500' :
                                  progressPercentage >= 50 ? 'bg-yellow-500' : 'bg-red-400'
            return (
              <div className="space-y-0.5">
                <div className="flex items-center justify-between text-[9px]">
                  <span className="text-gray-500">EMV Progress</span>
                  <span className={`font-medium ${
                    progressPercentage >= 80 ? 'text-green-600' :
                    progressPercentage >= 50 ? 'text-yellow-600' : 'text-red-500'
                  }`}>
                    {progressPercentage}%
                  </span>
                </div>
                <div className="h-1 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className={`h-full ${progressColor} rounded-full transition-all duration-300`}
                    style={{ width: `${progressPercentage}%` }}
                  />
                </div>
                <div className="flex items-center justify-between text-[8px] text-gray-400">
                  <span>{formatCurrency(totalEmv)}</span>
                  <span>{formatCurrency(targetValue)}</span>
                </div>
              </div>
            )
          })()}
        </div>
      )}

      {/* Bottom row - Actions left, Partner logo right */}
      <div className="flex items-center justify-between pt-1.5 border-t border-gray-100">
        {/* Action buttons */}
        <div className="flex items-center space-x-0.5">
          {onViewInsights && (
            <Tooltip content="View Insights" placement="top">
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  onViewInsights(activation)
                }}
                className="p-1 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-colors"
              >
                <Eye className="h-3 w-3" />
              </button>
            </Tooltip>
          )}
          <Tooltip content="Edit" placement="top">
            <button
              onClick={(e) => {
                e.stopPropagation()
                onEdit(activation)
              }}
              className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
            >
              <Edit2 className="h-3 w-3" />
            </button>
          </Tooltip>
          {onDuplicate && (
            <Tooltip content="Duplicate" placement="top">
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  onDuplicate(activation)
                }}
                className="p-1 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded transition-colors"
              >
                <Copy className="h-3 w-3" />
              </button>
            </Tooltip>
          )}
          <Tooltip content="Delete" placement="top">
            <button
              onClick={(e) => {
                e.stopPropagation()
                onDelete(activation)
              }}
              className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
            >
              <Trash2 className="h-3 w-3" />
            </button>
          </Tooltip>
        </div>

        {/* Partner logo - right side */}
        {partnerLogoUrl && !imageError && partner && (
          <Tooltip content={partnerName || 'Partner'} placement="top">
            <button
              onClick={(e) => {
                e.stopPropagation()
                if (onPartnerClick) {
                  onPartnerClick({
                    id: partner.id,
                    name: partner.name,
                    logo_url: partner.logo_url
                  })
                }
              }}
              className="cursor-pointer hover:opacity-80 transition-opacity"
            >
              <img
                src={partnerLogoUrl}
                alt={partnerName || 'Partner'}
                className="h-8 w-8 rounded object-contain bg-gray-50 flex-shrink-0"
                onError={() => setImageError(true)}
              />
            </button>
          </Tooltip>
        )}
      </div>
    </div>
  )
}

export default ActivationCard
